import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';
import { sendWelcomeEmail } from '@/lib/email';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Tables<'profiles'> | null;
  /** True only while initial auth session is being resolved. Does NOT include profile fetch. */
  loading: boolean;
  /** True while profile is being fetched/recovered for the current user. */
  profileLoading: boolean;
  /** True when authenticated user has no profile row (and recovery failed). */
  profileMissing: boolean;
  signOut: () => Promise<void>;
  refetchProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function recoverMissingProfile(user: User): Promise<Tables<'profiles'> | null> {
  try {
    const meta = (user.user_metadata || {}) as Record<string, any>;
    const fallbackName =
      meta.name ||
      meta.full_name ||
      meta.display_name ||
      (user.email ? user.email.split('@')[0] : 'Người chơi');

    const insertPayload: Record<string, any> = {
      user_id: user.id,
      name: fallbackName,
    };
    if (meta.avatar_url) insertPayload.avatar = meta.avatar_url;
    if (meta.skill_level) insertPayload.skill_level = meta.skill_level;
    if (meta.ranked_sport) insertPayload.ranked_sport = meta.ranked_sport;
    if (meta.city) insertPayload.city = meta.city;
    if (meta.phone_number) insertPayload.phone_number = meta.phone_number;
    if (meta.gender) insertPayload.gender = meta.gender;
    if (meta.birth_year) insertPayload.birth_year = meta.birth_year;

    const { data, error } = await supabase
      .from('profiles')
      .insert(insertPayload as any)
      .select('*')
      .maybeSingle();

    if (error) {
      console.warn('[Auth] Profile recovery insert failed:', error.message);
      // Race condition: row may already exist now (trigger created it). Re-fetch.
      const { data: existing } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      return existing ?? null;
    }
    return data ?? null;
  } catch (err) {
    console.error('[Auth] Profile recovery exception:', err);
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Tables<'profiles'> | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMissing, setProfileMissing] = useState(false);

  const fetchProfile = async (currentUser: User) => {
    setProfileLoading(true);
    setProfileMissing(false);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', currentUser.id)
        .maybeSingle();

      if (error) {
        console.warn('[Auth] Profile fetch error:', error.message);
        setProfile(null);
        setProfileMissing(true);
        return;
      }

      if (data) {
        setProfile(data);
        setProfileMissing(false);
        return;
      }

      // No profile row — try recovery
      const recovered = await recoverMissingProfile(currentUser);
      if (recovered) {
        setProfile(recovered);
        setProfileMissing(false);
      } else {
        setProfile(null);
        setProfileMissing(true);
      }
    } finally {
      setProfileLoading(false);
    }
  };

  const refetchProfile = async () => {
    if (user) {
      await fetchProfile(user);
    }
  };

  useEffect(() => {
    const welcomeSentRef = new Set<string>();

    // Listen for auth changes FIRST (before getSession to avoid missing events)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      setLoading(false);

      if (newSession?.user) {
        // Defer Supabase calls outside the callback to avoid deadlocks
        setTimeout(() => {
          fetchProfile(newSession.user);
        }, 0);

        if (
          event === 'SIGNED_IN' &&
          newSession.user.email &&
          !welcomeSentRef.has(newSession.user.id)
        ) {
          const createdAt = new Date(newSession.user.created_at);
          const now = new Date();
          const diffMinutes = (now.getTime() - createdAt.getTime()) / 60000;
          if (diffMinutes <= 5) {
            welcomeSentRef.add(newSession.user.id);
            const userName = newSession.user.user_metadata?.name || '';
            sendWelcomeEmail(newSession.user.email, userName).catch(console.error);
          }
        }
      } else {
        setProfile(null);
        setProfileMissing(false);
        setProfileLoading(false);
      }
    });

    // Then resolve initial session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      setLoading(false);
      if (initialSession?.user) {
        fetchProfile(initialSession.user);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        loading,
        profileLoading,
        profileMissing,
        signOut,
        refetchProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
